/**
 * Row assembly + validation. Turns the raw data grid plus a ColumnMapping into
 * ParsedRow[] with a deterministic row_key and a status: good rows are `ready`,
 * problem rows are `needs_attention` with a specific reason for the attention
 * tray (criteria 3, 5, 6). Address mode assembles component columns into a
 * single query string in country-aware order; coordinate mode range-validates
 * and catches out-of-range/obviously-swapped pairs.
 */

import type { AttentionReason, ColumnMapping, ColumnRole, ParsedRow } from "./types";
import { isLatValue, isLngValue } from "./recognizers";
import { classifyJunkRow } from "./structure";
import { rowKeyForAddress, rowKeyForCoord } from "./rowKey";

/** Country-aware assembly order (US-first: ZIP trails State). */
const US_ORDER: ColumnRole[] = ["address_full", "street", "city", "state", "zip", "country"];

/**
 * A geocodable row needs a street-level or locality anchor. Region-only codes
 * (zip/state/country) identify an AREA, not a point — the OSS default (US
 * Census) can't place a bare ZIP or state, so such a row is unplaceable and
 * must never be counted as "ready".
 */
const ANCHOR_ROLES: ColumnRole[] = ["address_full", "street", "city"];

const cell = (row: string[], idx: number | undefined): string =>
  idx === undefined ? "" : (row[idx] ?? "").trim();

/** True when the row supplies at least one street/locality anchor component. */
function hasLocalityAnchor(mapping: ColumnMapping, row: string[]): boolean {
  return ANCHOR_ROLES.some((role) => cell(row, mapping.roles[role]) !== "");
}

/**
 * True when the row carries a 5-digit US ZIP. A bare ZIP has no street, but it
 * IS placeable at its ZCTA centroid (MAP-129) via the postal backstop, so such
 * a row is `ready` even without a locality anchor.
 */
function hasUsZip(mapping: ColumnMapping, row: string[]): boolean {
  return /\b\d{5}\b/.test(cell(row, mapping.roles.zip));
}

/** Assemble a single geocode query from the mapped address-component columns. */
export function buildAddressQuery(mapping: ColumnMapping, row: string[]): string {
  const parts: string[] = [];
  for (const role of US_ORDER) {
    const v = cell(row, mapping.roles[role]);
    if (v !== "") parts.push(v);
  }
  return parts.join(", ");
}

/** Coordinate parse result for one row. */
export interface CoordResult {
  ok: boolean;
  lat?: number;
  lng?: number;
  reason?: AttentionReason;
}

/**
 * Parse + validate a lat/lng pair. Unparseable/missing -> bad_coords;
 * out-of-range or a value that can only be a swapped longitude (>90 in the lat
 * slot) -> out_of_range. Swaps where both values fall inside ±90 are
 * undetectable by range alone and pass through (documented limitation).
 */
export function parseCoords(latStr: string, lngStr: string): CoordResult {
  const a = latStr.trim();
  const b = lngStr.trim();
  if (a === "" || b === "") return { ok: false, reason: "bad_coords" };
  const lat = Number(a);
  const lng = Number(b);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { ok: false, reason: "bad_coords" };
  if (!isLatValue(a) || !isLngValue(b)) return { ok: false, reason: "out_of_range" };
  return { ok: true, lat, lng };
}

/** Build the validated, keyed row set from the data grid and mapping. */
export function buildRows(mapping: ColumnMapping, dataRows: string[][]): ParsedRow[] {
  return dataRows.map((cells) => {
    // Blank rows and trailing Total/summary rows are structural noise, not a
    // user error: drop them (status "skipped") so they never geocode, never
    // reach the attention tray, and never count against the user — the row's
    // reason is kept only for the quiet "N rows skipped" note.
    const junk = classifyJunkRow(cells);
    if (junk) return { row_key: "", cells, status: "skipped", attention_reason: junk };

    if (mapping.mode === "coordinate") {
      const res = parseCoords(cell(cells, mapping.roles.lat), cell(cells, mapping.roles.lng));
      if (!res.ok) {
        return { row_key: "", cells, status: "needs_attention", attention_reason: res.reason };
      }
      return { row_key: rowKeyForCoord(res.lat!, res.lng!), cells, status: "ready" };
    }

    const query = buildAddressQuery(mapping, cells);
    if (query === "") {
      return { row_key: "", cells, status: "needs_attention", attention_reason: "missing_address" };
    }
    // A query built only from region codes can't geocode to a street point —
    // BUT a bare US ZIP is placeable at its ZCTA centroid (MAP-129), so keep
    // those ready and let geocoding place them (or set them aside if the ZIP
    // isn't real). A region with no ZIP (state/country only) stays unplaceable.
    if (!hasLocalityAnchor(mapping, cells) && !hasUsZip(mapping, cells)) {
      return { row_key: "", cells, status: "needs_attention", attention_reason: "missing_locality" };
    }
    return { row_key: rowKeyForAddress(query), cells, status: "ready" };
  });
}

/**
 * Apply hand-entered cell fixes and run the same validation/keying path used
 * for imported rows. Keeping this pure makes inline remediation safe: a fixed
 * row cannot be marked ready until it satisfies the normal ingest rules.
 */
export function remediateRow(
  mapping: ColumnMapping,
  row: ParsedRow,
  edits: ReadonlyArray<{ column: number; value: string }>,
): ParsedRow {
  const cells = [...row.cells];
  for (const edit of edits) cells[edit.column] = edit.value;
  return buildRows(mapping, [cells])[0]!;
}

/**
 * Count ready / needs-attention / skipped for the summary + attention-tray
 * header. `skipped` (blank + summary rows) is tracked separately so it never
 * inflates the attention count the user is asked to act on.
 */
export function summarize(rows: ParsedRow[]): {
  ready: number;
  attention: number;
  skipped: number;
} {
  let ready = 0;
  let skipped = 0;
  for (const r of rows) {
    if (r.status === "ready") ready++;
    else if (r.status === "skipped") skipped++;
  }
  return { ready, attention: rows.length - ready - skipped, skipped };
}
